import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Enhanced data structure with multiple task categories
const data = [
  { date: 'Mon', completed: 4, inProgress: 2, pending: 1 },
  { date: 'Tue', completed: 3, inProgress: 3, pending: 2 },
  { date: 'Wed', completed: 7, inProgress: 1, pending: 1 },
  { date: 'Thu', completed: 5, inProgress: 2, pending: 3 },
  { date: 'Fri', completed: 8, inProgress: 1, pending: 0 },
  { date: 'Sat', completed: 2, inProgress: 4, pending: 1 },
  { date: 'Sun', completed: 3, inProgress: 2, pending: 2 },
];

// Custom color palette
const colors = {
  completed: 'hsl(142 72% 29%)',     // Emerald green
  inProgress: 'hsl(48 96% 53%)',      // Bright yellow
  pending: 'hsl(220 9% 87%)'          // Light gray
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  const total = payload.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <div className="rounded-lg border bg-background p-3 shadow-sm">
      <div className="text-sm font-medium">{label}</div>
      <div className="mt-1 space-y-1">
        {payload.map((entry) => (
          <div
            key={entry.name}
            className="flex items-center gap-2 text-sm"
          >
            <div
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">
              {entry.name}: {entry.value} tasks
            </span>
          </div>
        ))}
        <div className="pt-1 border-t text-sm text-muted-foreground">
          Total: {total} tasks
        </div>
      </div>
    </div>
  );
}

export function TaskChart() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Task Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="hsl(var(--border))"
              />
              <XAxis
                dataKey="date"
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="completed"
                name="Completed"
                fill={colors.completed}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="inProgress"
                name="In Progress"
                fill={colors.inProgress}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="pending"
                name="Pending"
                fill={colors.pending}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}